/**
 * H3 hex density utilities for spatial aggregation.
 *
 * Lazy-loads h3-js WASM module on first use to avoid blocking
 * initial page load.
 */

// Lazy-loaded h3-js module reference
let h3Module: typeof import('h3-js') | null = null;

/**
 * Lazy-load the h3-js WASM module.
 * Subsequent calls return the cached module.
 */
async function getH3(): Promise<typeof import('h3-js')> {
  if (!h3Module) {
    h3Module = await import('h3-js');
  }
  return h3Module;
}

/** Display resolution for hex overlay (~1.4km edge, city-block level) */
export const HEX_DISPLAY_RESOLUTION = 7;

export interface HexBucket {
  /** H3 cell index as hex string */
  cellId: string;
  /** Number of vehicles in this cell */
  count: number;
  /** Journey GIDs in this cell */
  journeyIds: string[];
  /** Cell boundary as [lat, lng][] ring */
  boundary: [number, number][];
}

/**
 * Aggregate vehicle positions into H3 hex buckets.
 *
 * @param positions Array of {latitude, longitude} objects
 * @returns Array of hex buckets with counts and boundaries
 */
export async function aggregateToHexBuckets(
  positions: Array<{ latitude: number; longitude: number; journeyId?: string }>,
  resolution: number = HEX_DISPLAY_RESOLUTION
): Promise<HexBucket[]> {
  const h3 = await getH3();

  // Collect vehicles per hex cell
  const cellJourneys = new Map<string, string[]>();
  const cellCounts = new Map<string, number>();
  for (const pos of positions) {
    const cellId = h3.latLngToCell(pos.latitude, pos.longitude, resolution);
    cellCounts.set(cellId, (cellCounts.get(cellId) ?? 0) + 1);
    if (pos.journeyId) {
      const ids = cellJourneys.get(cellId);
      if (ids) {
        ids.push(pos.journeyId);
      } else {
        cellJourneys.set(cellId, [pos.journeyId]);
      }
    }
  }

  // Build result with boundaries
  const buckets: HexBucket[] = [];
  for (const [cellId, count] of cellCounts) {
    const boundary = h3.cellToBoundary(cellId);
    buckets.push({ cellId, count, journeyIds: cellJourneys.get(cellId) ?? [], boundary });
  }

  return buckets;
}

/**
 * Convert hex buckets to GeoJSON FeatureCollection for MapLibre rendering.
 */
export function hexBucketsToGeoJSON(buckets: HexBucket[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: buckets.map((bucket) => ({
      type: 'Feature',
      properties: {
        cellId: bucket.cellId,
        count: bucket.count,
        journeyIds: bucket.journeyIds.join(','),
      },
      geometry: {
        type: 'Polygon',
        // H3 boundary is [lat, lng][] — GeoJSON needs [lng, lat][]
        coordinates: [
          [...bucket.boundary.map(([lat, lng]) => [lng, lat]),
          // Close the ring
          [bucket.boundary[0][1], bucket.boundary[0][0]]],
        ],
      },
    })),
  };
}
